type MetaEventParams = Record<string, string | number | boolean | undefined>;

type FbqFunction = {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[];
  loaded?: boolean;
  version?: string;
};

declare global {
  interface Window {
    fbq?: FbqFunction;
  }
}

export type MetaConversionPayload = {
  eventId: string;
  eventSourceUrl: string;
  fbp?: string;
  fbc?: string;
};

export function getMetaCookie(name: "_fbp" | "_fbc"): string | undefined {
  if (typeof document === "undefined") return undefined;
  return document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`))
    ?.split("=")[1];
}

/** Generate eventId + Meta cookie fields for CAPI deduplication (pass in API body). */
export function createMetaConversionPayload(): MetaConversionPayload {
  return {
    eventId: crypto.randomUUID(),
    eventSourceUrl: typeof window !== "undefined" ? window.location.href : "",
    fbp: getMetaCookie("_fbp"),
    fbc: getMetaCookie("_fbc"),
  };
}

/** Fire browser pixel on success using the same eventId sent to the backend. */
export function fireMetaPixelEvent(
  eventName: string,
  eventId: string,
  params?: MetaEventParams
) {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;
  window.fbq("track", eventName, params ?? {}, { eventID: eventId });
}

/** Fire a standard or custom Meta Pixel event when fbq is available. */
export const trackMetaEvent = (
  eventName: string,
  params?: MetaEventParams,
  options?: { custom?: boolean; eventID?: string }
) => {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;

  if (options?.custom) {
    window.fbq("trackCustom", eventName, params);
    return;
  }

  if (options?.eventID) {
    window.fbq("track", eventName, params ?? {}, { eventID: options.eventID });
    return;
  }

  if (params) {
    window.fbq("track", eventName, params);
  } else {
    window.fbq("track", eventName);
  }
};

export const trackMetaLead = (params?: MetaEventParams, eventID?: string) =>
  trackMetaEvent("Lead", params, eventID ? { eventID } : undefined);

export const trackMetaCompleteRegistration = (
  params?: MetaEventParams,
  eventID?: string
) =>
  trackMetaEvent("CompleteRegistration", params, eventID ? { eventID } : undefined);

export const trackMetaPurchase = (params: {
  value: number;
  currency?: string;
  content_ids?: string;
  content_type?: string;
}) =>
  trackMetaEvent("Purchase", {
    value: params.value,
    currency: params.currency || "USD",
    content_ids: params.content_ids,
    content_type: params.content_type || "product",
  });

export const trackMetaInitiateCheckout = (params?: MetaEventParams) =>
  trackMetaEvent("InitiateCheckout", params);

export const trackMetaViewContent = (params?: MetaEventParams) =>
  trackMetaEvent("ViewContent", params);

export const trackMetaSearch = (params?: MetaEventParams) =>
  trackMetaEvent("Search", params);

export const trackMetaContact = (params?: MetaEventParams) =>
  trackMetaEvent("Contact", params);

export const trackMetaSubmitApplication = (params?: MetaEventParams) =>
  trackMetaEvent("SubmitApplication", params);
